/**
 * PURPOSE: Editar de forma ordenada la identidad, estructura y reglas públicas de una disciplina.
 * RESPONSIBILITIES: Administrar config_category, formato Rugby y bestOf de Vóley sin sobrescribir reglas ajenas.
 * COLLABORATORS: CardConfigCategoryGeneral2.jsx, formatIdSport.js y Config_Category.js.
 * ROUTE: winscore/src/components/Cards/CardConfiguration/CardConfigCategoryGeneral/components/ConfigForm.jsx
 */
import React, { useEffect, useState } from "react";
import { formatIdSport } from "../../../../../utils/formatIdSport.js";
import { isAccumulatedSport } from "../../../../../utils/accumulatedStandingsDisplay.js";
import ImageUrlUploadField from "../../../../Inputs/ImageUrlUploadField.jsx";

const Toggle = ({ label, description, checked, onChange, disabled }) => (
  <label className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 transition hover:border-blue-200">
    <span className="min-w-0">
      <span className="block text-sm font-bold text-slate-800">{label}</span>
      {description ? (
        <span className="mt-1 block text-xs leading-5 text-slate-500">{description}</span>
      ) : null}
    </span>
    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={(event) => onChange(event.target.checked)}
      className="toggle-checkbox mt-0.5 shrink-0"
      disabled={disabled}
    />
  </label>
);

const RangeField = ({ label, value, onChange, min, max, disabled }) => (
  <label className="rounded-xl border border-slate-200 bg-white p-4">
    <span className="flex items-center justify-between gap-3 text-sm font-bold text-slate-700">
      {label}
      <span className="min-w-[36px] rounded-lg bg-blue-50 px-2 py-1 text-center text-blue-700">
        {value}
      </span>
    </span>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-4 w-full cursor-pointer accent-blue-600"
      disabled={disabled}
    />
  </label>
);

const Section = ({ eyebrow, title, description, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5 md:col-span-2">
    <div className="mb-4">
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
        {eyebrow}
      </p>
      <h3 className="mt-1 text-lg font-black text-slate-900">{title}</h3>
      {description ? (
        <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
      ) : null}
    </div>
    {children}
  </section>
);

const ConfigForm = ({ configData, isDisabled, onSave, onClose }) => {
  const [grupos, setGrupos] = useState(1);
  const [fechasGrupo, setFechasGrupo] = useState(1);
  const [etapasFinal, setEtapasFinal] = useState(1);
  const [participantes, setParticipantes] = useState(0);
  const [rondas, setRondas] = useState(1);
  const [isManual, setIsManual] = useState(false);
  const [isRandom, setIsRandom] = useState(false);
  const [useValidacionRegional, setUseValidacionRegional] = useState(false);
  const [useBandera, setUseBandera] = useState(false);
  const [useAcumulado, setUseAcumulado] = useState(false);
  const [useSembradoRegional, setUseSembradoRegional] = useState(false);
  const [defaultTabLinkPublico, setDefaultTabLinkPublico] = useState(3);
  const [clasificacionPorAcumulado, setClasificacionPorAcumulado] = useState(false);
  const [useAcumuladoV2, setUseAcumuladoV2] = useState(false);
  // Programación en PDF: columnas de uniforme visibles (1 por defecto, como siempre).
  const [showUniformColumnsPdf, setShowUniformColumnsPdf] = useState(true);
  const [useSanciones, setUseSanciones] = useState(false);
  const [useGoleadores, setUseGoleadores] = useState(false);
  const [useCabezaGrupo, setUseCabezaGrupo] = useState(false);
  const [useColumnaDeporteIntegral, setUseColumnaDeporteIntegral] = useState(false);
  const [configuracionSembradoRegion, setConfiguracionSembradoRegion] = useState(false);
  const [volleyballBestOf, setVolleyballBestOf] = useState(3);
  const [nameSport, setNameSport] = useState("");
  const [sportImage, setSportImage] = useState("");
  const [rugbyGameFormat, setRugbyGameFormat] = useState("sevens");
  const [equipos, setEquipos] = useState({ 3: false, 4: false, 5: false, 6: false });

  const formattedSport = formatIdSport(Number(configData?.idsport));
  const isVolleyball = formattedSport === 3;
  const isRugby = formattedSport === 6;
  // FTR-0065: la tabla acumulada nueva solo existe en fútbol, futsal, vóley y básquet.
  const supportsAccumulatedV2 = isAccumulatedSport(Number(configData?.idsport));

  useEffect(() => {
    if (!configData) return;
    setGrupos(configData.nro_grupos ?? 1);
    setFechasGrupo(configData.nro_fechas_grupo ?? 1);
    setEtapasFinal(configData.nro_etapas_final ?? 1);
    setParticipantes(configData.nro_instituciones_participantes ?? 0);
    setRondas(configData.nro_rondas ?? 1);
    setIsManual(Boolean(configData.isManual));
    setIsRandom(Boolean(configData.isRandom));
    setUseValidacionRegional(Boolean(configData.useValidacionRegional));
    setUseBandera(Boolean(configData.useBandera));
    setUseAcumulado(Boolean(configData.useAcumulado));
    setUseSembradoRegional(Boolean(configData.useSembradoRegional));
    setDefaultTabLinkPublico(configData.defaultTabLinkPublico ?? 3);
    setClasificacionPorAcumulado(Boolean(configData.clasificacionPorAcumulado));
    setUseAcumuladoV2(Boolean(configData.useAcumuladoV2));
    // Sin dato (backend anterior a la columna) => visibles; solo un 0 / false explícito las oculta.
    setShowUniformColumnsPdf(
      configData.showUniformColumnsPdf === undefined || configData.showUniformColumnsPdf === null
        ? true
        : Number(configData.showUniformColumnsPdf) !== 0 && configData.showUniformColumnsPdf !== false
    );
    setUseSanciones(Boolean(configData.useSanciones));
    setUseGoleadores(Boolean(configData.useGoleadores));
    setUseCabezaGrupo(Boolean(configData.useCabezaGrupo));
    setUseColumnaDeporteIntegral(Boolean(configData.useColumnaDeporteIntegral));
    setConfiguracionSembradoRegion(Number(configData.configuracion_sembrado_region) === 1);
    setVolleyballBestOf(
      Number(configData?.sport_rules?.volleyball?.bestOf) === 5 ? 5 : 3
    );
    setNameSport(configData.nameSport || "");
    setSportImage(configData.sport_image || "");
    setRugbyGameFormat(
      ["sevens", "quinces", "m18"].includes(configData.game_format)
        ? configData.game_format
        : "sevens"
    );

    const selectedTeams = String(configData.nro_equipos_x_grupo || "")
      .split(",")
      .map(Number);
    setEquipos({
      3: selectedTeams.includes(3),
      4: selectedTeams.includes(4),
      5: selectedTeams.includes(5),
      6: selectedTeams.includes(6),
    });
  }, [configData]);

  const handleSaveClick = () => {
    const nroEquiposXGrupo = Object.keys(equipos)
      .filter((key) => equipos[key])
      .join(", ");
    const sportRules = isVolleyball
      ? {
          ...(configData?.sport_rules || {}),
          version: 1,
          volleyball: {
            ...(configData?.sport_rules?.volleyball || {}),
            bestOf: volleyballBestOf,
          },
        }
      : configData?.sport_rules;

    onSave({
      grupos,
      fechasGrupo,
      etapasFinal,
      participantes,
      rondas,
      isManual,
      isRandom,
      nroEquiposXGrupo,
      useValidacionRegional,
      useBandera,
      useAcumulado,
      useSembradoRegional,
      defaultTabLinkPublico,
      clasificacionPorAcumulado,
      ...(supportsAccumulatedV2 ? { useAcumuladoV2 } : {}),
      showUniformColumnsPdf,
      useSanciones,
      useGoleadores,
      useCabezaGrupo,
      useColumnaDeporteIntegral,
      configuracion_sembrado_region: configuracionSembradoRegion ? 1 : 0,
      sport_rules: sportRules,
      nameSport,
      sport_image: sportImage,
      ...(isRugby ? { game_format: rugbyGameFormat } : {}),
    });
  };

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      <Section
        eyebrow="Presentación"
        title="Identidad pública"
        description="Personaliza el nombre y la imagen que se muestran en Winscore y los enlaces públicos."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <label className="grid gap-2 text-sm font-bold text-slate-700">
            Nombre visible del deporte
            <input
              type="text"
              value={nameSport}
              onChange={(event) => setNameSport(event.target.value)}
              placeholder="Usar nombre original"
              maxLength={255}
              disabled={isDisabled}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-normal outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
            />
          </label>
          <ImageUrlUploadField
            label="URL de imagen pública"
            value={sportImage}
            onChange={setSportImage}
            idevent={configData?.idevent}
            idsport={configData?.idsport}
            assetType="sport-image"
            disabled={isDisabled || !configData?.idevent || !configData?.idsport}
          />
        </div>

        {isRugby ? (
          <fieldset className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
            <legend className="px-1 text-sm font-black text-emerald-900">
              Formato de Rugby
            </legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {[
                { value: "sevens", label: "Sevens" },
                { value: "quinces", label: "Quinces" },
                { value: "m18", label: "M18" },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`cursor-pointer rounded-xl border px-4 py-3 text-center text-sm font-bold transition ${
                    rugbyGameFormat === option.value
                      ? "border-emerald-500 bg-white text-emerald-800 shadow-sm"
                      : "border-emerald-100 bg-white/70 text-slate-600"
                  }`}
                >
                  <input
                    type="radio"
                    name="rugby-game-format"
                    value={option.value}
                    checked={rugbyGameFormat === option.value}
                    onChange={() => setRugbyGameFormat(option.value)}
                    disabled={isDisabled}
                    className="sr-only"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
      </Section>

      <Section eyebrow="Torneo" title="Estructura de competencia">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <RangeField label="Número de grupos" value={grupos} onChange={setGrupos} min="0" max="10" disabled={isDisabled} />
          <RangeField label="Fechas primera fase" value={fechasGrupo} onChange={setFechasGrupo} min="0" max="10" disabled={isDisabled} />
          <RangeField label="Etapas finales" value={etapasFinal} onChange={setEtapasFinal} min="0" max="5" disabled={isDisabled} />
          <RangeField label="Participantes" value={participantes} onChange={setParticipantes} min="0" max="50" disabled={isDisabled} />
          <RangeField label="Rondas" value={rondas} onChange={setRondas} min="0" max="3" disabled={isDisabled} />
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-bold text-slate-700">Equipos por grupo</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {[3, 4, 5, 6].map((number) => (
                <label
                  key={number}
                  className={`cursor-pointer rounded-lg border px-3 py-2 text-sm font-black ${
                    equipos[number]
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-slate-200 text-slate-600"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={equipos[number]}
                    onChange={() =>
                      setEquipos((current) => ({
                        ...current,
                        [number]: !current[number],
                      }))
                    }
                    disabled={isDisabled}
                    className="sr-only"
                  />
                  {number}
                </label>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section eyebrow="Reglas" title="Clasificación y operación">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Toggle label="Configuración manual" checked={isManual} onChange={setIsManual} disabled={isDisabled} />
          <Toggle label="Sorteo aleatorio" checked={isRandom} onChange={setIsRandom} disabled={isDisabled} />
          <Toggle label="Validación regional" checked={useValidacionRegional} onChange={setUseValidacionRegional} disabled={isDisabled} />
          <Toggle label="Sembrado regional" checked={useSembradoRegional} onChange={setUseSembradoRegional} disabled={isDisabled} />
          <Toggle label="Cabeza de grupo" checked={useCabezaGrupo} onChange={setUseCabezaGrupo} disabled={isDisabled} />
          <Toggle label="Clasificación por acumulado" checked={clasificacionPorAcumulado} onChange={setClasificacionPorAcumulado} disabled={isDisabled} />
          {supportsAccumulatedV2 ? (
            <Toggle
              label="Tabla acumulada (nueva)"
              description="Ordena por puesto en el grupo y métricas por partido. El servidor la recalcula y se ve en Posiciones → Acumulada."
              checked={useAcumuladoV2}
              onChange={setUseAcumuladoV2}
              disabled={isDisabled}
            />
          ) : null}
          <Toggle label="Bloquear oficialización por planilla" checked={configuracionSembradoRegion} onChange={setConfiguracionSembradoRegion} disabled={isDisabled} />
        </div>
      </Section>

      <Section eyebrow="Publicación" title="Visualización y módulos públicos">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Toggle label="Mostrar bandera" checked={useBandera} onChange={setUseBandera} disabled={isDisabled} />
          <Toggle label="Tabla de acumulado legacy" checked={useAcumulado} onChange={setUseAcumulado} disabled={isDisabled} />
          <Toggle label="Módulo de sanciones" checked={useSanciones} onChange={setUseSanciones} disabled={isDisabled} />
          <Toggle label="Módulo de goleadores" checked={useGoleadores} onChange={setUseGoleadores} disabled={isDisabled} />
          <Toggle label="Columna deporte integral" checked={useColumnaDeporteIntegral} onChange={setUseColumnaDeporteIntegral} disabled={isDisabled} />
          <Toggle
            label="Uniformes en la programación (PDF)"
            description="Activo: la descarga de la programación muestra las columnas de uniforme (normal y arquero/líbero). Apagado: se ocultan y la columna Grupo se ensancha."
            checked={showUniformColumnsPdf}
            onChange={setShowUniformColumnsPdf}
            disabled={isDisabled}
          />
        </div>

        <label className="mt-4 grid max-w-xs gap-2 text-sm font-bold text-slate-700">
          Tab público predeterminado
          <input
            type="number"
            min="1"
            max="10"
            value={defaultTabLinkPublico}
            onChange={(event) => setDefaultTabLinkPublico(event.target.value)}
            disabled={isDisabled}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
          />
        </label>
      </Section>

      {isVolleyball ? (
        <Section
          eyebrow="Voleibol"
          title="Formato oficial"
          description="Controla la tabla pública y el valor inicial de las nuevas planillas."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {[3, 5].map((bestOf) => (
              <label
                key={bestOf}
                className={`cursor-pointer rounded-xl border px-4 py-3 text-center font-bold transition ${
                  volleyballBestOf === bestOf
                    ? "border-blue-500 bg-white text-blue-800 shadow-sm"
                    : "border-slate-200 bg-white/70 text-slate-600"
                }`}
              >
                <input
                  type="radio"
                  name="volleyball-best-of"
                  checked={volleyballBestOf === bestOf}
                  onChange={() => setVolleyballBestOf(bestOf)}
                  disabled={isDisabled}
                  className="sr-only"
                />
                Mejor de {bestOf} sets
              </label>
            ))}
          </div>
        </Section>
      ) : null}

      <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-between md:col-span-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isDisabled}
          className="rounded-xl border border-red-200 bg-red-50 px-5 py-2.5 font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cerrar configuración
        </button>
        <button
          type="button"
          onClick={handleSaveClick}
          disabled={isDisabled}
          className="rounded-xl bg-blue-600 px-6 py-2.5 font-bold text-white shadow-lg shadow-blue-500/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Guardar configuración
        </button>
      </div>
    </div>
  );
};

export default ConfigForm;
